import type {
  AuthResponse,
  MeResponse,
  ApiError,
  RunSubmission,
  SubmitRunResponse,
  ProgressResponse,
  DailyRun,
  LeaderboardResponse,
  LeaderboardMeResponse,
  RunSubmissionWithDaily,
  DailyStatusResponse,
  ClaimQuestResponse,
  QuestId,
  TelemetryEventRequest,
  TelemetryReportResponse,
  ShareCardResponse,
  HideoutResponse,
  HideoutUpgradeResult,
  HideoutModuleId,
  LoadoutResponse,
  PerkId,
  CatsResponse,
  CatClassId,
  ConsumableId,
  ConsumableLoadout,
  ConsumablesResponse,
  BuyConsumableResponse,
  ChallengeTodayResponse,
  TournamentCurrentResponse,
  SeasonResponse,
  ClaimMilestoneResponse,
} from '@cradle7/shared';
import { API_VERSION, API_VERSION_HEADER } from '@cradle7/shared';
import { setServerReachable } from '../networkStatus';

/** Typed error thrown from ApiClient. Lets UI code switch on `code`. */
export class ApiClientError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'ApiClientError';
  }
}

const TOKEN_KEY = 'cradle7.session.token';

/** Socket-level request timeout (ms). Guards against connections that open but
 *  never respond — the flaky-WebView failure mode that froze the app on boot. */
const REQUEST_TIMEOUT_MS = 12_000;

export class ApiClient {
  token: string | null;

  constructor(private baseUrl: string) {
    this.token = localStorage.getItem(TOKEN_KEY);
  }

  async authenticateTelegram(initData: string): Promise<AuthResponse> {
    const res = await this.req<AuthResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    });
    this.token = res.token;
    localStorage.setItem(TOKEN_KEY, res.token);
    return res;
  }

  async me(): Promise<MeResponse> {
    return this.req<MeResponse>('/me', { method: 'GET' });
  }

  /**
   * Send a telemetry event. NEVER throws (called from window.onerror — a throw
   * would re-enter the global handler). Intentionally bypasses the `req<T>`
   * pathway AND does NOT toggle `networkStatus` — telemetry must remain
   * independent of the main app's connectivity state, since the whole point is
   * to deliver crash data when other endpoints are dead.
   */
  async reportError(req: TelemetryEventRequest): Promise<TelemetryReportResponse | { ok: false }> {
    try {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (this.token) headers['authorization'] = `Bearer ${this.token}`;
      const res = await fetch(`${this.baseUrl}/telemetry/error`, {
        method: 'POST',
        headers,
        body: JSON.stringify(req),
      });
      if (!res.ok) return { ok: false };
      return (await res.json()) as TelemetryReportResponse;
    } catch {
      return { ok: false };
    }
  }

  async submitRun(submission: RunSubmissionWithDaily): Promise<SubmitRunResponse> {
    return this.req<SubmitRunResponse>('/runs', {
      method: 'POST',
      body: JSON.stringify(submission),
    });
  }

  async meProgress(): Promise<ProgressResponse> {
    return this.req<ProgressResponse>('/me/progress', { method: 'GET' });
  }

  async meDaily(): Promise<DailyStatusResponse> {
    return this.req<DailyStatusResponse>('/me/daily', { method: 'GET' });
  }

  async claimQuest(questId: QuestId): Promise<ClaimQuestResponse> {
    return this.req<ClaimQuestResponse>('/quests/claim', {
      method: 'POST',
      body: JSON.stringify({ questId }),
    });
  }

  async dailyRun(): Promise<DailyRun> {
    return this.req<DailyRun>('/daily-run', { method: 'GET' });
  }

  async leaderboardDaily(opts: { date?: string; limit?: number } = {}): Promise<LeaderboardResponse> {
    const params = new URLSearchParams();
    if (opts.date) params.set('date', opts.date);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    const qs = params.toString();
    const path = qs ? `/leaderboard/daily?${qs}` : '/leaderboard/daily';
    return this.req<LeaderboardResponse>(path, { method: 'GET' });
  }

  async leaderboardDailyMe(opts: { date?: string } = {}): Promise<LeaderboardMeResponse> {
    const params = new URLSearchParams();
    if (opts.date) params.set('date', opts.date);
    const qs = params.toString();
    const path = qs ? `/leaderboard/daily/me?${qs}` : '/leaderboard/daily/me';
    return this.req<LeaderboardMeResponse>(path, { method: 'GET' });
  }

  async shareCard(runId: string): Promise<ShareCardResponse> {
    return this.req<ShareCardResponse>(`/runs/${encodeURIComponent(runId)}/share-card`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async meHideout(): Promise<HideoutResponse> {
    return this.req<HideoutResponse>('/me/hideout', { method: 'GET' });
  }

  async hideoutUpgrade(moduleId: HideoutModuleId): Promise<HideoutUpgradeResult> {
    return this.req<HideoutUpgradeResult>('/hideout/upgrade', {
      method: 'POST',
      body: JSON.stringify({ moduleId }),
    });
  }

  async meLoadout(): Promise<LoadoutResponse> {
    return this.req<LoadoutResponse>('/me/loadout', { method: 'GET' });
  }

  async setLoadout(perkId: PerkId | null): Promise<LoadoutResponse> {
    return this.req<LoadoutResponse>('/loadout', {
      method: 'POST',
      body: JSON.stringify({ perkId }),
    });
  }

  async meCats(): Promise<CatsResponse> {
    return this.req<CatsResponse>('/me/cats', { method: 'GET' });
  }

  async adoptCat(classId: CatClassId): Promise<CatsResponse> {
    return this.req<CatsResponse>('/me/cats/adopt', {
      method: 'POST',
      body: JSON.stringify({ classId }),
    });
  }

  meConsumables(): Promise<ConsumablesResponse> {
    return this.req<ConsumablesResponse>('/me/consumables', { method: 'GET' });
  }

  buyConsumable(itemId: ConsumableId): Promise<BuyConsumableResponse> {
    return this.req<BuyConsumableResponse>('/consumables/buy', {
      method: 'POST',
      body: JSON.stringify({ itemId }),
    });
  }

  setConsumablesLoadout(slots: ConsumableLoadout): Promise<ConsumablesResponse> {
    return this.req<ConsumablesResponse>('/consumables/loadout', {
      method: 'POST',
      body: JSON.stringify({ slots }),
    });
  }

  /**
   * Phase 2i — GET /challenge/today. Lazy-creates today's daily challenge if
   * missing, returns the player's entry (or null), their rank, the top-10
   * leaderboard, and the prize tier config.
   */
  async getChallenge(): Promise<ChallengeTodayResponse> {
    return this.req<ChallengeTodayResponse>('/challenge/today', { method: 'GET' });
  }

  /**
   * Phase 2k — GET /tournament/current. Returns an inactive placeholder on
   * weekdays and the cumulative weekend leaderboard on Fri/Sat/Sun.
   */
  async getTournament(): Promise<TournamentCurrentResponse> {
    return this.req<TournamentCurrentResponse>('/tournament/current', { method: 'GET' });
  }

  /**
   * Phase 3l — GET /me/season. Returns the player's current season state
   * including xp, level, milestones, claimedLevels, and claimableLevels.
   */
  async getSeason(): Promise<SeasonResponse> {
    return this.req<SeasonResponse>('/me/season', { method: 'GET' });
  }

  /**
   * Phase 3l — POST /me/season/claim. Claims a milestone reward for the given
   * level. Returns the updated claimedLevels list and the new fishies balance.
   */
  async claimMilestone(level: number): Promise<ClaimMilestoneResponse> {
    return this.req<ClaimMilestoneResponse>('/me/season/claim', {
      method: 'POST',
      body: JSON.stringify({ level }),
    });
  }

  /**
   * Phase 2u: fire-and-forget analytics. Errors are swallowed — never
   * blocks the run. Returns void.
   */
  async reportTelemetryEvent(
    kind: 'perk.picked' | 'perk.skipped',
    payload: { perkId: string; runId: string; offerIndex: number },
  ): Promise<void> {
    try {
      await this.req('/telemetry/event', {
        method: 'POST',
        body: JSON.stringify({ kind, payload }),
      });
    } catch {
      // analytics must never break the run
    }
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem(TOKEN_KEY);
  }

  private async req<T>(path: string, init: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    };
    if (this.token) headers['authorization'] = `Bearer ${this.token}`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, { ...init, headers, signal: controller.signal });
    } catch (e) {
      setServerReachable(false);
      throw e;
    } finally {
      clearTimeout(timeout);
    }
    setServerReachable(true);

    // Wire-protocol version check — surface client/server skew explicitly.
    // Lenient on telemetry-style 204s with no body / no header (no JSON to parse).
    const versionHeader = res.headers.get(API_VERSION_HEADER);
    if (versionHeader !== null) {
      const serverVersion = Number(versionHeader);
      if (!Number.isFinite(serverVersion) || serverVersion !== API_VERSION) {
        throw new ApiClientError(
          'version_mismatch',
          `API version mismatch: client expects ${API_VERSION}, server sent ${versionHeader}`,
        );
      }
    }

    if (!res.ok) {
      let err: ApiError = { error: `HTTP ${res.status}`, code: 'http_error' };
      try {
        err = await res.json() as ApiError;
      } catch {}
      throw new ApiClientError(err.code, `${err.code}: ${err.error}`);
    }
    return await res.json() as T;
  }
}
